<template>
  <nav>
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType"
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />

    <v-row
      v-if="role == 'booster'"
      class="mx-auto"
      :class="mobile() ? 'pa-3' : ''"
    >
      <v-col
        :cols="mobile() ? '12' : '11'"
        class="mx-auto px-0 d-flex justify-end"
      >
        <v-btn
          color="accent"
          class="rounded-xxl text-none px-4 white--text"
          elevation="0"
          data-testid="add-reminder-button"
          @click="openCreateDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('addReminderRule') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row
      v-if="(!reminderRules || reminderRules.length === 0) && !loadingData"
      align="center"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <EmptyPage
          :page-title="$t('emptyActivityRemindersTitle')"
          :page-text="$t('emptyActivityRemindersText')"
          page-image="image5"
        />
      </v-col>
    </v-row>

    <v-row
      v-else-if="reminderRules.length > 0"
      align="center"
      class="mx-auto mt-2"
      :class="mobile() ? 'pa-3' : ''"
    >
      <v-col
        :cols="mobile() ? '12' : '11'"
        class="mx-auto px-0"
      >
        <v-card
          v-for="rule in reminderRules"
          :key="rule.id"
          rounded="xxl"
          class="my-2 solo"
        >
          <v-hover>
            <v-row
              slot-scope="{ hover }"
              no-gutters
              :class="`py-4 elevation-${hover ? 8 : 2}`"
            >
              <v-col cols="10" class="px-6">
                <div class="text-body-2 grey--text">
                  {{ typeLabel(rule.attributes.reminder_type) }}
                </div>
                <div class="text-body-1 darktext--text">
                  {{ ruleSummary(rule) }}
                </div>
                <div class="text-body-2 darktext--text mt-2">
                  {{ rule.attributes.email_subject }}
                </div>
              </v-col>
              <v-col cols="2" class="d-flex align-center justify-end pr-4">
                <v-menu offset-y flat bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" data-testid="reminder-actions-menu">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list data-testid="menu-list-reminder">
                    <v-list-item
                      data-testid="view-reminder"
                      @click="openViewDialog(rule)"
                    >
                      <v-list-item-title>{{ $t('viewReminderRule') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      data-testid="delete-reminder"
                      @click="confirmDelete(rule)"
                    >
                      <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </v-hover>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialogVisible"
      max-width="640"
      overlay-color="darktext"
      overlay-opacity="0.6"
      persistent
      scrollable
    >
      <v-card class="pa-2">
        <v-card-title>
          {{ $t('addReminderRule') }}
        </v-card-title>
        <v-card-text style="max-height: 60vh;">
          <p class="text-body-2 greytext--text mb-4">
            {{ $t('activityRemindersHelpText') }}
          </p>
          <v-form ref="form" v-model="valid" @submit.prevent>
            <p class="text-body-2 font-weight-bold mb-2 darktext--text">
              {{ $t('reminderTypeLabel') }}
            </p>
            <v-select
              v-model="form.reminder_type"
              data-testid="reminder-type-select"
              :items="reminderTypeOptions"
              item-text="text"
              item-value="value"
              solo
              elevation="6"
              hide-details
              class="rounded-xl text-body-2"
              :rules="[rulesRequired]"
            />
            <p
              v-if="selectedTypeExplanation"
              class="text-body-2 greytext--text mt-0 mb-0 px-2"
              data-testid="reminder-type-explanation"
            >
              {{ selectedTypeExplanation }}
            </p>

            <template v-if="form.reminder_type === TYPE_DEADLINE">
              <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
                {{ $t('reminderDeadlineDateLabel') }}
              </p>
              <v-menu
                v-model="deadlineDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.deadline_date"
                    data-testid="reminder-deadline-date-input"
                    readonly
                    solo
                    elevation="6"
                    class="rounded-xl text-body-2"
                    :placeholder="$t('reminderDeadlineDatePlaceholder')"
                    :rules="[rulesRequired]"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="accent">mdi-calendar</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.deadline_date"
                  no-title
                  @input="deadlineDateMenu = false"
                />
              </v-menu>
            </template>

            <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
              {{ daysFieldLabel }}
            </p>
            <v-text-field
              v-model.number="form.days"
              data-testid="reminder-days-input"
              type="number"
              min="1"
              solo
              elevation="6"
              class="rounded-xl text-body-2"
              :rules="[rulesRequired, rulesMinDays]"
            />
            <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
              {{ $t('labelSubject') }}
            </p>
            <v-text-field
              v-model="form.email_subject"
              data-testid="reminder-subject-input"
              solo
              elevation="6"
              class="rounded-xl text-body-2"
              :rules="[rulesRequired]"
            />
            <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
              {{ $t('labelBody') }}
            </p>
            <v-textarea
              v-model="form.email_message"
              data-testid="reminder-message-input"
              solo
              elevation="6"
              rows="5"
              hide-details
              class="rounded-xl text-body-2"
              :rules="[rulesRequired]"
            />
            <p
              class="text-body-2 greytext--text mt-1 mb-0"
              data-testid="reminder-body-explanation"
            >
              {{ $t('reminderBodyActivityUrlExplanation') }}
            </p>
            <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
              {{ $t('labelShortCodes') }}
            </p>
            <div class="text-body-2 darktext--text">
              <p class="mb-1">
                [ACTIVITY_TITLE] - {{ $t('reminderPlaceholderActivityTitle') }}
              </p>
              <p class="mb-0">
                [USER_NAME] - {{ $t('reminderPlaceholderUserName') }}
              </p>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            color="darktext"
            class="rounded-xxl text-none px-4 white--text mr-2"
            elevation="0"
            @click="closeDialog"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="accent"
            class="rounded-xxl text-none px-4 white--text"
            elevation="0"
            :loading="saving"
            :disabled="!valid"
            data-testid="save-reminder-button"
            @click="saveRule"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="viewDialogVisible"
      max-width="640"
      overlay-color="darktext"
      overlay-opacity="0.6"
      scrollable
    >
      <v-card class="pa-2" data-testid="view-reminder-dialog">
        <v-row no-gutters class="pa-4 pb-0">
          <v-col cols="10">
            <div class="text-h6 darktext--text">
              {{ $t('viewReminderRuleTitle') }}
            </div>
          </v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-icon
              data-testid="view-reminder-close"
              @click="closeViewDialog"
            >
              mdi-close
            </v-icon>
          </v-col>
        </v-row>
        <v-card-text v-if="viewedRule" class="px-6 pb-6 pt-4">
          <p class="text-body-2 font-weight-bold mb-1 darktext--text">
            {{ $t('reminderTypeLabel') }}
          </p>
          <p class="text-body-1 darktext--text mb-4" data-testid="view-reminder-type">
            {{ typeLabel(viewedRule.attributes.reminder_type) }}
          </p>

          <template v-if="viewedRule.attributes.reminder_type === TYPE_DEADLINE">
            <p class="text-body-2 font-weight-bold mb-1 darktext--text">
              {{ $t('reminderDeadlineDateLabel') }}
            </p>
            <p class="text-body-1 darktext--text mb-4" data-testid="view-reminder-deadline">
              {{ formatDeadlineDate(viewedRule.attributes.deadline_date) || '—' }}
            </p>
          </template>

          <p class="text-body-2 font-weight-bold mb-1 darktext--text">
            {{ viewedDaysFieldLabel }}
          </p>
          <p class="text-body-1 darktext--text mb-4" data-testid="view-reminder-days">
            {{ viewedRule.attributes.days }}
          </p>

          <p class="text-body-2 font-weight-bold mb-1 darktext--text">
            {{ $t('labelSubject') }}
          </p>
          <p class="text-body-1 darktext--text mb-4" data-testid="view-reminder-subject">
            {{ viewedRule.attributes.email_subject }}
          </p>

          <p class="text-body-2 font-weight-bold mb-1 darktext--text">
            {{ $t('labelBody') }}
          </p>
          <p
            class="text-body-1 darktext--text mb-0"
            style="white-space: pre-wrap;"
            data-testid="view-reminder-message"
          >
            {{ viewedRule.attributes.email_message }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      v-if="showDeleteDialog"
      :visible="showDeleteDialog"
      :cd-title="$t('deleteReminderRuleTitle')"
      :cd-text="$t('deleteReminderRuleText')"
      :cd-submit-btn="$t('delete')"
      @close="showDeleteDialog = false"
      @proceedSubmit="deleteRule"
    />
  </nav>
</template>

<script>
import i18n from '../../plugins/i18n'
import {
  getActivityReminderRules,
  createActivityReminderRule,
  deleteActivityReminderRule,
} from '../../api/activityApi'
import EmptyPage from '../../components/EmptyPage'
import AlertMessages from '../../components/AlertMessages'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'

const TYPE_NOT_STARTED = 'not_started'
const TYPE_DEADLINE = 'deadline'

export default {
  components: {
    EmptyPage,
    AlertMessages,
    ConfirmationDialog,
  },
  mixins: [repeatedMethodsMixin],
  props: {
    role: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      TYPE_NOT_STARTED,
      TYPE_DEADLINE,
      activityId: this.$route.params.id,
      reminderRules: [],
      loadingData: false,
      dialogVisible: false,
      viewDialogVisible: false,
      viewedRule: null,
      saving: false,
      valid: false,
      showDeleteDialog: false,
      ruleToDelete: null,
      deadlineDateMenu: false,
      alertMsg: '',
      alertType: '',
      alertValue: false,
      form: {
        reminder_type: TYPE_NOT_STARTED,
        days: 3,
        deadline_date: null,
        email_subject: '',
        email_message: '',
      },
      rulesRequired: (v) => !!v || i18n.t('required'),
      rulesMinDays: (v) => (Number(v) >= 1) || i18n.t('reminderDaysMinError'),
    }
  },
  computed: {
    reminderTypeOptions() {
      return [
        { value: TYPE_NOT_STARTED, text: i18n.t('reminderNotStartedLabel') },
        { value: TYPE_DEADLINE, text: i18n.t('reminderDeadlineLabel') },
      ]
    },
    daysFieldLabel() {
      if (this.form.reminder_type === TYPE_DEADLINE) {
        return i18n.t('reminderDaysBeforeDeadlineLabel')
      }
      return i18n.t('reminderDaysLabel')
    },
    selectedTypeExplanation() {
      if (this.form.reminder_type === TYPE_DEADLINE) {
        return i18n.t('reminderDeadlineTypeExplanation')
      }
      if (this.form.reminder_type === TYPE_NOT_STARTED) {
        return i18n.t('reminderNotStartedTypeExplanation')
      }
      return ''
    },
    viewedDaysFieldLabel() {
      if (
        this.viewedRule &&
        this.viewedRule.attributes.reminder_type === TYPE_DEADLINE
      ) {
        return i18n.t('reminderDaysBeforeDeadlineLabel')
      }
      return i18n.t('reminderDaysLabel')
    },
  },
  async created() {
    if (this.role && this.role !== 'booster') {
      this.$router.push({ name: 'PageAccessDenied' })
      return
    }
    this.loadingData = true
    await this.fetchRules()
    this.loadingData = false
  },
  methods: {
    typeLabel(type) {
      if (type === TYPE_DEADLINE) {
        return i18n.t('reminderDeadlineLabel')
      }
      return i18n.t('reminderNotStartedLabel')
    },
    ruleSummary(rule) {
      const days = rule.attributes.days
      if (rule.attributes.reminder_type === TYPE_DEADLINE) {
        return this.$tc('reminderDaysBeforeDeadline', days, {
          days,
          date: this.formatDeadlineDate(rule.attributes.deadline_date) || '—',
        })
      }
      return this.$tc('reminderDaysAfterAdded', days, { days })
    },
    formatDeadlineDate(value) {
      if (!value) {
        return null
      }
      return String(value).slice(0, 10)
    },
    emptyForm() {
      return {
        reminder_type: TYPE_NOT_STARTED,
        days: 3,
        deadline_date: null,
        email_subject: '',
        email_message: '',
      }
    },
    buildAttributes() {
      const attributes = {
        days: Number(this.form.days),
        email_subject: this.form.email_subject,
        email_message: this.form.email_message,
      }

      if (this.form.reminder_type === TYPE_DEADLINE) {
        attributes.deadline_date = this.formatDeadlineDate(this.form.deadline_date)
      }

      return attributes
    },
    async fetchRules() {
      try {
        const response = await getActivityReminderRules(this.activityId)
        if (response.status === 200) {
          this.reminderRules = response.data.data || []
        }
      } catch (e) {
        this.handleError(e)
      }
    },
    openCreateDialog() {
      this.form = this.emptyForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },
    openViewDialog(rule) {
      this.viewedRule = rule
      this.viewDialogVisible = true
    },
    closeViewDialog() {
      this.viewDialogVisible = false
      this.viewedRule = null
    },
    closeDialog() {
      this.dialogVisible = false
      this.deadlineDateMenu = false
    },
    async saveRule() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.saving = true
      try {
        await createActivityReminderRule({
          data: {
            type: 'activity-reminder-rules',
            attributes: {
              reminder_type: this.form.reminder_type,
              ...this.buildAttributes(),
            },
            relationships: {
              activity: {
                data: {
                  type: 'activities',
                  id: this.activityId,
                },
              },
            },
          },
        })
        this.alertMsg = i18n.t('reminderRuleCreated')
        this.alertType = 'success'
        this.alertValue = true
        this.closeDialog()
        await this.fetchRules()
      } catch (e) {
        this.handleError(e)
      } finally {
        this.saving = false
      }
    },
    confirmDelete(rule) {
      this.ruleToDelete = rule
      this.showDeleteDialog = true
    },
    async deleteRule() {
      if (!this.ruleToDelete) {
        return
      }
      try {
        await deleteActivityReminderRule(this.ruleToDelete.id)
        this.showDeleteDialog = false
        this.ruleToDelete = null
        this.alertType = 'success'
        this.alertMsg = i18n.t('reminderRuleDeleted')
        this.alertValue = true
        await this.fetchRules()
      } catch (e) {
        this.showDeleteDialog = false
        this.handleError(e)
      }
    },
    handleError(e) {
      if (!e.response) {
        return
      }
      this.alertValue = true
      if (e.response.status === 403) {
        this.$router.push({ name: 'PageAccessDenied' })
      } else if (e.response.status === 404) {
        this.$router.push({ name: 'PageNotFound' })
      } else {
        this.alertType = 'error'
        this.alertMsg = i18n.t('serverError')
      }
    },
  },
}
</script>
